/**
 * A drawn mark for an item, chosen from what the item actually is.
 *
 *  The Associated Items panel showed the same grey ▤ against a HEPA filter, a
 *  copper pipe and a cable tray. It is a placeholder standing where a product
 *  photograph would be in Books, and it told a reader nothing — seven identical
 *  tiles down a bill of materials.
 *
 *  Line art rather than emoji, for two reasons. Emoji are a font: they render
 *  differently on every machine, and the only cleanroom-adjacent ones — 🔩 ⚙️ —
 *  are a small and arbitrary set that runs out immediately. These are drawn at
 *  the size they are used, inherit `currentColor` so they take the category's
 *  own colour, and read at 44px.
 *
 *  Chosen by what the item is called first and by its category second, because
 *  A09 SUB PRODUCT holds filters, dampers and diffusers and they are not the
 *  same object. Where neither says anything, a plain box — an honest "no idea"
 *  rather than a wrong picture.
 */

import React from "react";

const S = { fill: "none", stroke: "currentColor", strokeWidth: 1.6,
            strokeLinecap: "round", strokeLinejoin: "round" };

/** Each glyph draws inside a 24×24 box. */
const GLYPHS = {
  // A pleated filter media pack in its frame.
  filter: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="1" {...S} />
      <path d="M6 6.5 8 12l-2 5.5M9.5 6.5 11.5 12l-2 5.5M13 6.5 15 12l-2 5.5M16.5 6.5 18.5 12l-2 5.5" {...S} />
    </>
  ),
  // Impeller in a housing — FFU, blower, fan section.
  fan: (
    <>
      <circle cx="12" cy="12" r="8.5" {...S} />
      <circle cx="12" cy="12" r="2" {...S} />
      <path d="M12 10c0-3 1-5 3.5-5.5M14 12c3 0 5 1 5.5 3.5M12 14c0 3-1 5-3.5 5.5M10 12c-3 0-5-1-5.5-3.5" {...S} />
    </>
  ),
  // A rectangular duct run, drawn in the isometric a sheet-metal drawing uses.
  duct: (
    <>
      <path d="M3 8.5 8 5.5h13v10l-5 3H3z" {...S} />
      <path d="M8 5.5v10M8 15.5H3M21 15.5l-5 3" {...S} />
    </>
  ),
  // Hex head and threaded shank.
  fastener: (
    <>
      <path d="M9 3.2 12 1.8l3 1.4v3.2L12 7.8 9 6.4z" {...S} />
      <path d="M10.5 7.8h3v13l-1.5 2-1.5-2z" {...S} />
      <path d="M10.5 11h3M10.5 14h3M10.5 17h3" {...S} />
    </>
  ),
  // Conductor and a terminated end.
  cable: (
    <>
      <path d="M3 17c3.5 0 3.5-10 7-10s3.5 10 7 10" {...S} />
      <rect x="17" y="14" width="4.5" height="6" rx="1" {...S} />
      <path d="M19.2 14v-2" {...S} />
    </>
  ),
  // Perforated cable tray, seen along its length.
  tray: (
    <>
      <path d="M3 7v10h18V7" {...S} />
      <path d="M3 7h18" {...S} />
      <circle cx="8" cy="12" r="0.9" {...S} />
      <circle cx="12" cy="12" r="0.9" {...S} />
      <circle cx="16" cy="12" r="0.9" {...S} />
    </>
  ),
  // Pipe with flanged ends.
  pipe: (
    <>
      <path d="M6 8h12v8H6z" {...S} />
      <path d="M4.5 6.5v11M19.5 6.5v11" {...S} />
    </>
  ),
  // Valve body with a handwheel — also serves a damper.
  valve: (
    <>
      <path d="M4 9v6l8-3zM20 9v6l-8-3z" {...S} />
      <path d="M12 12V6M9 5h6" {...S} />
    </>
  ),
  // An L-section bracket, the thing a patti or angle actually is.
  bracket: (
    <>
      <path d="M5 3.5h4.5v13H20V21H5z" {...S} />
      <circle cx="7.2" cy="18.8" r="0.9" {...S} />
      <circle cx="17.5" cy="18.8" r="0.9" {...S} />
      <circle cx="7.2" cy="6" r="0.9" {...S} />
    </>
  ),
  // Panel or enclosure door with a handle.
  panel: (
    <>
      <rect x="4" y="3" width="16" height="18" rx="1" {...S} />
      <path d="M15.5 11v2.5" {...S} />
      <path d="M4 7h16" {...S} />
    </>
  ),
  // A dial instrument.
  instrument: (
    <>
      <circle cx="12" cy="12" r="8.5" {...S} />
      <path d="M12 12l4-3" {...S} />
      <path d="M12 3.5v2M20.5 12h-2M12 20.5v-2M3.5 12h2" {...S} />
    </>
  ),
  // Spanner — a service or a job of work rather than a thing.
  service: (
    <>
      <path d="M15.5 3.5a5 5 0 0 0-4.2 7.6L3.5 18.9 5.1 20.5l7.8-7.8a5 5 0 0 0 6.4-6.5l-2.9 2.9-2.6-.6-.6-2.6z" {...S} />
    </>
  ),
  // Luminaire.
  light: (
    <>
      <path d="M8.5 15a5.5 5.5 0 1 1 7 0c-.8.7-1 1.3-1 2.5h-5c0-1.2-.2-1.8-1-2.5z" {...S} />
      <path d="M10 20h4" {...S} />
    </>
  ),
  // Motor with its shaft and terminal box.
  motor: (
    <>
      <rect x="3.5" y="7" width="12" height="10" rx="1" {...S} />
      <path d="M15.5 11h3.5v2h-3.5M7 4.5h5V7" {...S} />
      <path d="M6 10v4M9 10v4M12 10v4" {...S} />
    </>
  ),
  // Air terminal / diffuser, seen from below.
  diffuser: (
    <>
      <rect x="3" y="3" width="18" height="18" rx="1" {...S} />
      <path d="M6.5 6.5 12 12l5.5-5.5M6.5 17.5 12 12l5.5 5.5" {...S} />
    </>
  ),
  // Parts making one thing — a composite parent.
  composite: (
    <>
      <rect x="3" y="3" width="8" height="8" rx="1" {...S} />
      <rect x="13" y="3" width="8" height="8" rx="1" {...S} />
      <rect x="3" y="13" width="8" height="8" rx="1" {...S} />
      <path d="M13 17h8M17 13v8" {...S} />
    </>
  ),
  // Sheet, plate, coil stock.
  sheet: (
    <>
      <path d="M3 7.5 12 3l9 4.5-9 4.5z" {...S} />
      <path d="M3 12 12 16.5 21 12M3 16.5 12 21l9-4.5" {...S} />
    </>
  ),
  box: (
    <>
      <path d="M3 7.5 12 3l9 4.5v9L12 21l-9-4.5z" {...S} />
      <path d="M3 7.5 12 12l9-4.5M12 12v9" {...S} />
    </>
  ),
};

/** Name fragments, most specific first — the first hit wins.
 *
 *  Order is the whole design here, and two real items proved it. "GI Cable Tray
 *  for Copper Piping and Cabling" drew a pipe because `piping` was tested before
 *  `cable tray`, and "25000 CFM AHU LOUVERS" drew a fan because `ahu` was tested
 *  before `louver`. The noun that says what the object IS goes first; the words
 *  describing what it serves come after.
 */
const BY_NAME = [
  [/\bcable tray|\btray\b/i, "tray"],
  [/\bhepa|filter|pleat|media\b/i, "filter"],
  [/\bdiffuser|grill|grille|louver|louvre|terminal box|air terminal\b/i, "diffuser"],
  [/\bffu|blower|\bfan\b|impeller|ahu\b/i, "fan"],
  [/\bduct|ducting|elbow|plenum\b/i, "duct"],
  [/\bdamper|valve|cock|actuator\b/i, "valve"],
  [/\bpipe|piping|tube|tubing|copper|refnet|drain\b/i, "pipe"],
  [/\bcable|wire|wiring|cabling|conductor|lug\b/i, "cable"],
  [/\bbolt|screw|nut|washer|rivet|stud|anchor|fastener|thread\b/i, "fastener"],
  [/\bbracket|angle|patti|channel|support|stand|frame|hanger|clamp\b/i, "bracket"],
  [/\bpanel|enclosure|door|partition|ceiling|wall|board\b/i, "panel"],
  [/\bmotor\b/i, "motor"],
  [/\blight|luminaire|lamp|led\b/i, "light"],
  [/\bgauge|meter|sensor|manometer|thermometer|instrument|monitor\b/i, "instrument"],
  [/\bsheet|plate|coil|strip|foil\b/i, "sheet"],
  [/\bservice|installation|labour|labor|charges|testing|validation|sitc\b/i, "service"],
];

/** Category, when the name gives nothing away. */
const BY_CATEGORY = {
  A01: "fastener", A02: "cable", A03: "pipe", A04: "box", A05: "sheet",
  A06: "fan", A07: "panel", A08: "instrument", A09: "filter",
  A10: "service", A11: "service", A12: "composite",
};

export function glyphKeyFor(code = "", name = "") {
  for (const [re, key] of BY_NAME) if (re.test(name)) return key;
  const cat = String(code).slice(0, 3).toUpperCase();
  return BY_CATEGORY[cat] || "box";
}

/** The mark itself. `size` is the box; the glyph is drawn to fill it. */
export default function ItemGlyph({ code = "", name = "", size = 24,
                                    color = "currentColor", title }) {
  const key = glyphKeyFor(code, name);
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" role="img"
         aria-label={title || key} style={{ color, display: "block" }}>
      {title ? <title>{title}</title> : null}
      {GLYPHS[key]}
    </svg>
  );
}
